import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DocUpload from "@/components/DocUpload";
import AgencyChronology, { LogContactModal } from "@/components/AgencyChronology";
import FilingCabinet from "@/components/FilingCabinet";
import { RESOURCES, ASSET_FIELDS, EXPOSURE_FIELDS, CONTACT_FIELDS, LIFE_EVENT_FIELDS, CLIENT_FIELDS, badgeClass } from "@/lib/config";
import RecordDialog from "@/components/RecordDialog";
import RoleBadges from "@/components/RoleBadges";
import DataTable, { fmtMoney, fmtDate, fmtDateTime } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Phone, Mail, Plus, ArrowLeft, ShieldAlert, Anchor, Users, Building2,
  FileText, CircleDollarSign, ClipboardList, AlertTriangle, CalendarClock, FolderOpen, Pencil,
} from "lucide-react";

const TYPE_ICON = { household: Users, organization: Building2, person: Users };

function Panel({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-white border border-border rounded-sm">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-cloud">
        <h3 className="font-serif text-lg text-navy flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-atlantic" />}{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-border rounded-sm px-4 py-3">
      <div className="font-serif text-2xl text-navy">{value}</div>
      <div className="text-[11px] text-mutedsea mt-0.5">{label}</div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [showLogContact, setShowLogContact] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [chronoTypes, setChronoTypes] = useState([]);
  useEffect(() => { api.get("/chronology/activity-types").then(({ data }) => setChronoTypes(data.types || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/parties/${id}/summary`); setS(data); }
    catch { toast.error("Could not load client."); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (s?.party) api.post("/agency/recent", { resource: "parties", record_id: s.party.id, label: s.party.display_name, party_id: s.party.id }).catch(() => {});
  }, [s?.party?.id]);

  if (!s) return <div className="p-6 text-mutedsea">Loading client…</div>;
  const p = s.party;
  const c = s.counts;
  const Icon = TYPE_ICON[p.party_type] || Users;

  const open = (fields, endpoint, extra = {}, method = "post", title = "New Record") =>
    setDialog({ fields, endpoint, initial: { party_id: id, ...extra }, method, title });
  const openEdit = (fields, key, rec, title) =>
    setDialog({ fields, endpoint: `/${key}/${rec.id}`, initial: rec, method: "put", title });

  const logActivity = (type) => open(RESOURCES.activities.fields, "/activities",
    { activity_type: type, direction: "Outbound", timestamp: new Date().toISOString(), owner: "" }, "post", `Log ${type}`);

  const openDoc = async (doc) => {
    if (doc.dropbox_file_id) {
      try {
        const { data } = await api.get(`/dropbox/documents/${doc.id}/link`);
        window.open(data.link, "_blank");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not open document."); }
    } else {
      openEdit(RESOURCES.documents.fields, "documents", doc, "Document Details");
    }
  };

  // timeline
  const timeline = [...(s.activities || [])].slice(0, 40);

  return (
    <div className="p-6 animate-fade-in">
      <button onClick={() => nav("/clients")} data-testid="back-clients" className="text-sm text-atlantic hover:text-ocean flex items-center gap-1 mb-3 nav-link">
        <ArrowLeft className="h-4 w-4" /> All Clients
      </button>

      {/* Header */}
      <div className="bg-deepnavy text-white rounded-sm p-5 mb-5 relative overflow-hidden">
        <Anchor className="absolute -right-6 -bottom-6 h-40 w-40 text-white/5" />
        <div className="flex items-start justify-between gap-4 relative">
          <div>
            <div className="flex items-center gap-2 text-sky/70 text-xs uppercase tracking-wide mb-1">
              <Icon className="h-4 w-4" />
              <span data-testid="client-entity-type">{{ person: "Individual", organization: "Organization", household: "Household", legal_entity: "Organization" }[p.party_type] || "Party"}</span>
              · <span data-testid="client-status">{p.status}</span>
            </div>
            <RoleBadges roles={p.relationship_roles || [p.relationship || "Client"]}
                        businessRelationship={!!p.business_relationship_id}
                        testid="client-role-badges" className="mb-1" />
            <h1 className="font-serif text-4xl leading-tight" data-testid="client-name">{p.display_name}</h1>
            {(p.dba || p.legal_name) && (
              <div className="text-sm text-sky/80 mt-1">
                {p.legal_name && <span>{p.legal_name}</span>}
                {p.dba && <span data-testid="client-dba"> · DBA: {p.dba}</span>}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
              {p.service_tier && <Badge className="bg-atlantic text-white border-0">Tier: {p.service_tier}</Badge>}
              {p.owner_user_id && <span className="text-sky/80">Producer: {p.owner_user_id}</span>}
              {p.preferred_communication && <span className="text-sky/80">Prefers: {p.preferred_communication}</span>}
              {p.marketing_permission === false && <Badge className="bg-seaamber/20 text-seaamber border-seaamber/40"><ShieldAlert className="h-3 w-3 mr-1" />No Marketing Consent</Badge>}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {can.write && <>
              {(p.relationship_roles || [p.relationship]).includes("Prospect") && !(p.relationship_roles || [p.relationship]).includes("Client") && <Button data-testid="convert-to-client" onClick={() => setShowConvert(true)} size="sm" className="bg-seagreen hover:bg-seagreen/90 text-white font-medium"><Users className="h-4 w-4 mr-1.5" /> Convert to Client</Button>}
              <Button data-testid="header-log-contact" onClick={() => setShowLogContact(true)} size="sm" className="bg-white text-navy hover:bg-white/90 font-medium"><Phone className="h-4 w-4 mr-1.5" /> Log Contact</Button>
              <Button data-testid="log-call" onClick={() => logActivity("Phone")} size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20"><Phone className="h-4 w-4 mr-1.5" /> Log Call</Button>
              <Button data-testid="log-email" onClick={() => logActivity("Email")} size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20"><Mail className="h-4 w-4 mr-1.5" /> Log Email</Button>
              <Button data-testid="edit-client" onClick={() => openEdit(CLIENT_FIELDS[p.party_type] || CLIENT_FIELDS.person, "parties", p, "Edit Client")} size="sm" variant="outline" className="bg-transparent text-white border-white/20 hover:bg-white/10"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
            </>}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
        <StatCard label="Active Policies" value={c.policies} />
        <StatCard label="Annual Premium" value={fmtMoney(c.annual_premium)} />
        <StatCard label="Open Cases" value={c.open_cases} />
        <StatCard label="Upcoming Renewals" value={c.upcoming_renewals} />
        <StatCard label="Open Claims" value={c.open_claims} />
        <StatCard label="Open Tasks" value={c.open_tasks} />
        <StatCard label="Documents" value={c.documents} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-border">
          {["overview", "policies", "household", "service", "claims", "renewals", "chronology", "documents", "opportunities"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize data-[state=active]:bg-navy data-[state=active]:text-white">{t === "household" ? (p.party_type === "organization" ? "Exposures" : "Household") : t === "chronology" ? "Agency Chronology" : t}</TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 space-y-4">
              <Panel title="Insurance Program" icon={FileText}>
                <MiniTable rows={s.policies} onRowClick={(r) => nav(`/policies/${r.id}`)}
                  cols={[{ key: "line", label: "Line" }, { key: "carrier_name", label: "Carrier" }, { key: "policy_number", label: "Policy #" }, { key: "premium", label: "Premium", money: true }, { key: "expiration_date", label: "Expires", date: true }, { key: "status", label: "Status", badge: true }]} />
              </Panel>
              <Panel title="Recent Timeline" icon={CalendarClock}>
                {timeline.length === 0 ? <p className="text-sm text-mutedsea">No activity recorded.</p> :
                  <div className="space-y-3">
                    {timeline.map((a) => (
                      <div key={a.id} className="flex gap-3 text-sm">
                        <div className="w-1 rounded bg-sky shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={badgeClass(a.activity_type)}>{a.activity_type}</Badge>
                            <span className="text-slate font-medium">{a.subject}</span>
                            <span className="text-xs text-mutedsea ml-auto">{fmtDateTime(a.timestamp)}</span>
                          </div>
                          {a.summary && <p className="text-mutedsea text-xs mt-1">{a.summary}</p>}
                        </div>
                      </div>
                    ))}
                  </div>}
              </Panel>
            </div>
            <div className="space-y-4">
              <Panel title="Contact" icon={Users}>
                <dl className="text-sm space-y-1.5">
                  <Row k="Email" v={p.email} /><Row k="Phone" v={p.phone} />
                  <Row k="Address" v={[p.address_street, p.address_city, p.address_state, p.address_zip].filter(Boolean).join(", ")} />
                  <Row k="Occupation" v={p.occupation} /><Row k="Industry" v={p.industry} />
                </dl>
              </Panel>
              <Panel title={p.party_type === "organization" ? "Key Contacts" : "Household Members"} icon={Users}
                action={can.write && p.party_type !== "person" && <AddBtn onClick={() => p.party_type === "organization" ? open(CONTACT_FIELDS, "/contacts", {}, "post", "New Contact") : open(CLIENT_FIELDS.person, "/parties", { party_type: "person", household_id: id }, "post", "New Member")} />}>
                {p.party_type === "person" ? <p className="text-sm text-mutedsea">Individual client — no household members.</p> :
                 p.party_type === "organization" ?
                  (s.contacts.length === 0 ? <Empty /> : s.contacts.map((ct) => (
                    <div key={ct.id} className="flex justify-between text-sm py-1 border-b border-border last:border-0 cursor-pointer hover:bg-ice px-1" onClick={() => openEdit(CONTACT_FIELDS, "contacts", ct, "Edit Contact")}>
                      <span className="text-slate">{ct.name}</span><span className="text-mutedsea text-xs">{ct.role}</span>
                    </div>))) :
                  (s.members.length === 0 ? <Empty /> : s.members.map((m) => (
                    <div key={m.id} className="flex justify-between text-sm py-1 border-b border-border last:border-0 cursor-pointer hover:bg-ice px-1" onClick={() => nav(`/clients/${m.id}`)}>
                      <span className="text-slate">{m.display_name}</span><span className="text-mutedsea text-xs">{m.role}</span>
                    </div>)))}
              </Panel>
              <Panel title="Service & Attention" icon={ClipboardList}>
                <dl className="text-sm space-y-1.5">
                  <Row k="Open Cases" v={c.open_cases} /><Row k="Open Tasks" v={c.open_tasks} />
                  <Row k="Open Claims" v={c.open_claims} /><Row k="Open Opportunities" v={c.opportunities} />
                </dl>
              </Panel>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="policies" className="mt-4">
          <SectionAdd label="Policy" can={can.write} onAdd={() => open(RESOURCES.policies.fields, "/policies", {}, "post", "New Policy")} />
          <MiniTable rows={s.policies} onRowClick={(r) => nav(`/policies/${r.id}`)}
            cols={RESOURCES.policies.columns} />
        </TabsContent>

        <TabsContent value="household" className="mt-4 space-y-4">
          {p.party_type === "organization" ? (
            <Panel title="Commercial Exposures" icon={Building2} action={can.write && <AddBtn onClick={() => open(EXPOSURE_FIELDS, "/exposures", {}, "post", "New Exposure")} />}>
              <MiniTable rows={s.exposures} onRowClick={(r) => openEdit(EXPOSURE_FIELDS, "exposures", r, "Edit Exposure")}
                cols={[{ key: "exposure_type", label: "Type", badge: true }, { key: "description", label: "Description" }, { key: "address", label: "Address" }, { key: "validation_status", label: "Validation", badge: true }, { key: "effective_date", label: "Effective", date: true }]} />
            </Panel>
          ) : (
            <>
              <Panel title="Assets — Drivers, Vehicles, Residences" icon={Anchor}
                action={can.write && <AssetAdd onPick={(t) => open(ASSET_FIELDS[t], "/assets", { asset_type: t }, "post", `New ${t}`)} />}>
                <MiniTable rows={s.assets} onRowClick={(r) => openEdit(ASSET_FIELDS[r.asset_type] || ASSET_FIELDS.Vehicle, "assets", r, `Edit ${r.asset_type}`)}
                  cols={[{ key: "asset_type", label: "Type", badge: true }, { key: "description", label: "Description" }, { key: "vin", label: "VIN" }, { key: "usage", label: "Usage" }]} />
              </Panel>
              <Panel title="Life Events" icon={CalendarClock} action={can.write && <AddBtn onClick={() => open(LIFE_EVENT_FIELDS, "/life_events", {}, "post", "New Life Event")} />}>
                <MiniTable rows={s.life_events} onRowClick={(r) => openEdit(LIFE_EVENT_FIELDS, "life_events", r, "Edit Life Event")}
                  cols={[{ key: "event_type", label: "Event", badge: true }, { key: "event_date", label: "Date", date: true }, { key: "notes", label: "Notes" }]} />
              </Panel>
            </>
          )}
        </TabsContent>

        <TabsContent value="service" className="mt-4 space-y-4">
          <Panel title="Service Cases" icon={ClipboardList} action={can.write && <AddBtn onClick={() => open(RESOURCES.service_cases.fields, "/service_cases", {}, "post", "New Service Case")} />}>
            <MiniTable rows={s.service_cases} onRowClick={(r) => openEdit(RESOURCES.service_cases.fields, "service_cases", r, "Edit Service Case")} cols={RESOURCES.service_cases.columns} />
          </Panel>
          <Panel title="Tasks" icon={ClipboardList} action={can.write && <AddBtn onClick={() => open(RESOURCES.tasks.fields, "/tasks", {}, "post", "New Task")} />}>
            <MiniTable rows={s.tasks} onRowClick={(r) => openEdit(RESOURCES.tasks.fields, "tasks", r, "Edit Task")} cols={RESOURCES.tasks.columns} />
          </Panel>
        </TabsContent>

        <TabsContent value="claims" className="mt-4">
          <SectionAdd label="Claim" can={can.write} onAdd={() => open(RESOURCES.claims.fields, "/claims", {}, "post", "New Claim")} />
          <MiniTable rows={s.claims} onRowClick={(r) => openEdit(RESOURCES.claims.fields, "claims", r, "Edit Claim")} cols={RESOURCES.claims.columns} />
        </TabsContent>

        <TabsContent value="renewals" className="mt-4">
          <MiniTable rows={s.renewals} onRowClick={(r) => openEdit(RESOURCES.renewals.fields, "renewals", r, "Edit Renewal")} cols={RESOURCES.renewals.columns} />
        </TabsContent>

        <TabsContent value="chronology" className="mt-4">
          <AgencyChronology partyId={id} canWrite={can.write} />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <FilingCabinet partyId={id} policies={s.policies} canWrite={can.write} canArchive={can.archive} />
        </TabsContent>

        <TabsContent value="opportunities" className="mt-4">
          <SectionAdd label="Opportunity" can={can.write} onAdd={() => open(RESOURCES.opportunities.fields, "/opportunities", {}, "post", "New Opportunity")} />
          <MiniTable rows={s.opportunities} onRowClick={(r) => openEdit(RESOURCES.opportunities.fields, "opportunities", r, "Edit Opportunity")} cols={RESOURCES.opportunities.columns} />
        </TabsContent>
      </Tabs>

      {dialog && (
        <RecordDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}
          fields={dialog.fields} title={dialog.title} initial={dialog.initial}
          endpoint={dialog.endpoint} method={dialog.method}
          onSaved={() => { setDialog(null); load(); }} />
      )}
      <LogContactModal open={showLogContact} onOpenChange={setShowLogContact} partyId={id}
        types={chronoTypes} onSaved={() => load()} />
      <ConvertToClientDialog open={showConvert} onOpenChange={setShowConvert} party={p}
        onDone={() => { setShowConvert(false); load(); }} />
    </div>
  );
}

function ConvertToClientDialog({ open, onOpenChange, party, onDone }) {
  const [owners, setOwners] = useState([]);
  const [producer, setProducer] = useState(party?.owner_user_id || "");
  const [clientSince, setClientSince] = useState(new Date().toISOString().slice(0, 10));
  const [tier, setTier] = useState(party?.service_tier || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setProducer(party?.owner_user_id || ""); api.get("/lookups/owners").then(({ data }) => setOwners(data.items || [])).catch(() => {}); } }, [open, party]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/parties/${party.id}/convert-to-client`, { producer, client_since: clientSince, service_tier: tier });
      toast.success("Prospect converted to Client — full history preserved.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Conversion failed."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="convert-client-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Convert to Client</DialogTitle>
          <DialogDescription className="text-xs text-mutedsea">Same record — all chronology, notes, documents, opportunities, contacts and audit history are preserved. No duplicate is created.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs mb-1 block">Assigned Producer</Label>
            <Select value={producer || "__none"} onValueChange={(v) => setProducer(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="convert-producer" className="bg-white text-sm"><SelectValue placeholder="Select producer…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64"><SelectItem value="__none">— unassigned —</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs mb-1 block">Client Effective Date</Label>
            <Input data-testid="convert-date" type="date" value={clientSince} onChange={(e) => setClientSince(e.target.value)} className="bg-white text-sm" /></div>
          <div><Label className="text-xs mb-1 block">Service Tier (optional)</Label>
            <Input data-testid="convert-tier" value={tier} onChange={(e) => setTier(e.target.value)} placeholder="e.g. Standard" className="bg-white text-sm" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="convert-submit" onClick={submit} disabled={busy} className="bg-seagreen hover:bg-seagreen/90 text-white">{busy ? "Converting…" : "Convert to Client"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => (<div className="flex justify-between gap-4"><dt className="text-mutedsea">{k}</dt><dd className="text-slate text-right">{v || "—"}</dd></div>);
const Empty = () => <p className="text-sm text-mutedsea">None recorded.</p>;
const AddBtn = ({ onClick }) => <Button size="sm" variant="outline" onClick={onClick} data-testid="panel-add" className="h-7 text-xs border-atlantic text-atlantic hover:bg-ice"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>;
const SectionAdd = ({ label, onAdd, can }) => can ? <div className="mb-3 flex justify-end"><Button size="sm" onClick={onAdd} data-testid={`add-${label.toLowerCase()}`} className="bg-navy hover:bg-atlantic text-white"><Plus className="h-4 w-4 mr-1.5" /> New {label}</Button></div> : null;

function AssetAdd({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button size="sm" variant="outline" data-testid="asset-add" onClick={() => setOpen(!open)} className="h-7 text-xs border-atlantic text-atlantic hover:bg-ice"><Plus className="h-3.5 w-3.5 mr-1" /> Add Asset</Button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 bg-white border border-border rounded-sm shadow-md w-40" onMouseLeave={() => setOpen(false)}>
          {Object.keys(ASSET_FIELDS).map((t) => (
            <button key={t} data-testid={`asset-type-${t.toLowerCase().replace(/\s/g, "-")}`} onClick={() => { setOpen(false); onPick(t); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-ice nav-link">{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniTable({ rows, cols, onRowClick }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-mutedsea py-2">None recorded.</p>;
  return (
    <div className="overflow-x-auto end-scroll">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border">{cols.map((c) => <th key={c.key} className="text-left text-[11px] uppercase tracking-wide text-mutedsea font-semibold px-2 py-1.5 whitespace-nowrap">{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`mini-row-${r.id}`} onClick={() => onRowClick && onRowClick(r)} className={`border-b border-border last:border-0 hover:bg-ice ${onRowClick ? "cursor-pointer" : ""}`}>
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-1.5 whitespace-nowrap">
                  {c.badge ? (r[c.key] ? <Badge variant="outline" className={`font-normal ${badgeClass(r[c.key])}`}>{r[c.key]}</Badge> : "—")
                    : c.money ? fmtMoney(r[c.key]) : c.date ? fmtDate(r[c.key]) : (r[c.key] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
